/**
 * View-scale presets (issue #95): the camera's vertical FOV normally shows
 * everything at roughly ×0.47 of true apparent size (a 60° vertical FOV vs.
 * the ~27° a desktop monitor subtends at normal viewing distance). This
 * module is the pure decision logic for the framing control — True scale
 * (27°, the Landscape Institute TGN 06/19 50mm full-frame-equivalent
 * convention), Explore (60°, the previous fixed FOV) and, since issue #177,
 * Official sheet (90° HORIZONTAL, ~21mm) — kept DOM/three.js free so it is
 * unit-tested in plain Node; main.ts owns wiring it to the HUD button, the
 * `V` key, and `camera.fov` (matching the ui/qualityControl.ts split of pure
 * decision logic vs. DOM glue).
 *
 * Presentation-only: FOV never feeds any LOS result or its display (hard
 * constraint #1).
 */

/** Boot default and the "true to life" preset (parent spec / issue #95). */
export const TRUE_SCALE_FOV_DEG = 27;
/** The previous fixed FOV, kept as the wide-overview preset. */
export const EXPLORE_FOV_DEG = 60;

/**
 * Official-sheet preset (issue #177): the developer's wirelines are "90 Degree
 * View" panoramas, which the Landscape Institute TGN 06/19 treats as context
 * only, never for judging scale. The 90° here is HORIZONTAL — the vertical FOV
 * three.js actually wants is derived from the live viewport aspect
 * (`officialSheetFraming`), so the horizon width stays 90° whatever shape the
 * window is. Explore's 60° *vertical* only approximates this at 16:9 and drifts
 * with the window, which is exactly what this preset fixes.
 */
export const OFFICIAL_SHEET_HFOV_DEG = 90;

/**
 * Vertical-FOV ceiling for the official-sheet preset. A tall, narrow viewport
 * needs an ever-larger vertical FOV to keep 90° horizontal, and the projection
 * degenerates as it approaches 180°. 140° is well clear of that and still wide
 * enough that every realistic device — including a 9:19.5 phone held portrait,
 * which needs ~130° — keeps the full 90° horizontally; only a genuine sliver of
 * a window (aspect below ~0.36) clamps. Once clamped the horizontal FOV really
 * is narrower than 90°, and `officialSheetFraming` reports that honestly rather
 * than claiming 90° with a caveat attached.
 */
export const MAX_OFFICIAL_SHEET_VFOV_DEG = 140;

/**
 * Vertical-FOV floor for the official-sheet preset. Only reachable at absurd
 * aspects (wider than ~115:1), where the derived vertical FOV would otherwise
 * collapse towards zero and make `focalLengthMmEquivalent` divide by ~0. Keeps
 * every derived number finite — never a degenerate or NaN projection (hard
 * constraint #7).
 */
export const MIN_OFFICIAL_SHEET_VFOV_DEG = 1;

/**
 * Aspect used when the caller's aspect is missing or nonsense (NaN, ±Infinity,
 * zero or negative — e.g. `camera.aspect` computed from a zero-height canvas
 * during a rotation). Silence is safe *here specifically*: this is a
 * presentation-only framing choice with no analytical meaning, the alternative
 * (throwing on the resize path) would blank the viewer, and the value
 * self-corrects on the very next resize with a real viewport. The 16:9
 * reference is also the aspect the official sheets themselves are drawn at.
 */
export const FALLBACK_ASPECT = 16 / 9;

/** Full-frame sensor half-height (mm) used for the 35mm-equivalent maths. */
const FULL_FRAME_HALF_HEIGHT_MM = 12;

const FOV_MATCH_EPSILON = 1e-6;

/**
 * 35mm (full-frame)-equivalent focal length for a vertical FOV:
 * `round(12 / tan(fov/2))`. 27° -> 50mm, 60° -> 21mm.
 */
export function focalLengthMmEquivalent(fovDeg: number): number {
  const halfAngleRad = (fovDeg * Math.PI) / 180 / 2;
  return Math.round(FULL_FRAME_HALF_HEIGHT_MM / Math.tan(halfAngleRad));
}

export type ViewScalePreset = 'true-scale' | 'explore' | 'official-sheet' | 'custom';
/** The presets the toggle can actually select (everything but 'custom', which
 * is only ever *arrived at*, e.g. by a viewpoint's explicit `fov_deg`). */
export type NamedViewScalePreset = Exclude<ViewScalePreset, 'custom'>;

/**
 * Which fixed-FOV preset (if any) a vertical FOV matches. Anything that isn't
 * one of the two fixed presets — e.g. an explicit viewpoint `fov_deg` — is
 * 'custom'.
 *
 * Deliberately blind to 'official-sheet' (issue #177): that preset's vertical
 * FOV depends on the live aspect, so a FOV alone cannot identify it, and at
 * some aspects it coincides exactly with 27° or 60°. main.ts therefore tracks
 * the active preset explicitly and only uses this to classify a FOV something
 * else set — see `presetAfterExternalFov`.
 */
export function presetForFov(fovDeg: number): ViewScalePreset {
  if (Math.abs(fovDeg - TRUE_SCALE_FOV_DEG) < FOV_MATCH_EPSILON) return 'true-scale';
  if (Math.abs(fovDeg - EXPLORE_FOV_DEG) < FOV_MATCH_EPSILON) return 'explore';
  return 'custom';
}

const DEG = Math.PI / 180;

/** What the official-sheet preset resolves to at a given viewport aspect. */
export interface OfficialSheetFraming {
  /** Vertical FOV to hand to the camera, degrees. Always finite, always
   * within [MIN_OFFICIAL_SHEET_VFOV_DEG, MAX_OFFICIAL_SHEET_VFOV_DEG]. */
  verticalFovDeg: number;
  /** Horizontal FOV actually achieved, degrees — 90 unless a clamp bit. */
  horizontalFovDeg: number;
  /** True when a clamp bit and the horizontal FOV is therefore not 90°. */
  clamped: boolean;
}

/** Horizontal FOV (degrees) a vertical FOV subtends at `aspect`. */
function horizontalFovDeg(verticalFovDeg: number, aspect: number): number {
  return (2 * Math.atan(Math.tan((verticalFovDeg * DEG) / 2) * aspect)) / DEG;
}

/**
 * Resolves the official-sheet preset (issue #177) at a viewport aspect
 * (width / height): the vertical FOV that makes the horizontal FOV exactly
 * `OFFICIAL_SHEET_HFOV_DEG`, clamped at both ends so the projection can never
 * degenerate. Pure; presentation-only (hard constraint #1).
 */
export function officialSheetFraming(aspect: number): OfficialSheetFraming {
  const a = Number.isFinite(aspect) && aspect > 0 ? aspect : FALLBACK_ASPECT;
  const wanted = (2 * Math.atan(Math.tan((OFFICIAL_SHEET_HFOV_DEG * DEG) / 2) / a)) / DEG;
  if (wanted > MAX_OFFICIAL_SHEET_VFOV_DEG) {
    return {
      verticalFovDeg: MAX_OFFICIAL_SHEET_VFOV_DEG,
      horizontalFovDeg: horizontalFovDeg(MAX_OFFICIAL_SHEET_VFOV_DEG, a),
      clamped: true,
    };
  }
  if (wanted < MIN_OFFICIAL_SHEET_VFOV_DEG) {
    return {
      verticalFovDeg: MIN_OFFICIAL_SHEET_VFOV_DEG,
      horizontalFovDeg: horizontalFovDeg(MIN_OFFICIAL_SHEET_VFOV_DEG, a),
      clamped: true,
    };
  }
  return { verticalFovDeg: wanted, horizontalFovDeg: OFFICIAL_SHEET_HFOV_DEG, clamped: false };
}

/** The vertical FOV a preset resolves to at the current viewport aspect. Only
 * 'official-sheet' looks at the aspect; the other two are fixed, exactly as
 * before (issue #95). */
export function fovForPreset(preset: NamedViewScalePreset, aspect: number): number {
  if (preset === 'true-scale') return TRUE_SCALE_FOV_DEG;
  if (preset === 'explore') return EXPLORE_FOV_DEG;
  return officialSheetFraming(aspect).verticalFovDeg;
}

/**
 * The preset the toggle/`V` key switches to: True scale -> Explore -> Official
 * sheet -> True scale, with any custom FOV (e.g. one just applied by a
 * viewpoint jump) landing on True scale first, after which it cycles normally.
 */
export function nextViewScalePreset(current: ViewScalePreset): NamedViewScalePreset {
  if (current === 'true-scale') return 'explore';
  if (current === 'explore') return 'official-sheet';
  return 'true-scale';
}

/**
 * Re-derives the active preset after something outside this control set
 * `camera.fov` directly (a viewpoint jump carrying an explicit `fov_deg`).
 * Keeps the previous preset when the FOV still matches what that preset
 * resolves to at this aspect — so an aspect-dependent official-sheet framing
 * survives a jump that didn't touch the FOV — and otherwise classifies the FOV
 * on its own merits.
 */
export function presetAfterExternalFov(
  previous: ViewScalePreset,
  fovDeg: number,
  aspect: number,
): ViewScalePreset {
  if (previous !== 'custom' && Math.abs(fovDeg - fovForPreset(previous, aspect)) < FOV_MATCH_EPSILON) {
    return previous;
  }
  return presetForFov(fovDeg);
}

/** HUD toggle button label for the current vertical FOV. */
export function viewScaleButtonLabel(fovDeg: number): string {
  const preset = presetForFov(fovDeg);
  if (preset === 'true-scale') return `View: true scale (${focalLengthMmEquivalent(fovDeg)}mm)`;
  if (preset === 'explore') return `View: explore (${focalLengthMmEquivalent(fovDeg)}mm)`;
  return `View: custom (${Math.round(fovDeg)}°)`;
}

/**
 * HUD toggle button label for the active preset (issue #177). True scale and
 * Explore read exactly as they always did — this delegates to
 * `viewScaleButtonLabel` for them, so their wording is one definition, not two.
 * Official sheet leads with the HORIZONTAL angle, since that is the number the
 * official wireline sheets are quoted at, followed by the 35mm-equivalent of
 * the derived vertical FOV (~21mm at 16:9). If a clamp bit, the narrower
 * horizontal that is genuinely rendered is what gets shown.
 */
export function viewScalePresetLabel(
  preset: ViewScalePreset,
  fovDeg: number,
  aspect: number,
): string {
  if (preset !== 'official-sheet') return viewScaleButtonLabel(fovDeg);
  const framing = officialSheetFraming(aspect);
  const mm = focalLengthMmEquivalent(framing.verticalFovDeg);
  return `View: official sheet (${Math.round(framing.horizontalFovDeg)}° / ${mm}mm)`;
}

/** Whether the viewing-distance hint should be shown — True scale only. */
export function isTrueScaleActive(fovDeg: number): boolean {
  return presetForFov(fovDeg) === 'true-scale';
}
